/**
 * Guards de domínio do endpoint de convite (`POST /companies/invite-doctor`),
 * sobre a infra canônica de `@/utils/apiError`. Usado por CreateInviteModal e
 * pelo reenvio de convite na Detail do médico (CompanyInviteRow).
 *
 * Aceitam tanto o erro cru do axios quanto um `ServiceError` (erro já
 * normalizado por `Service.handleError` e relançado pela store): o código e o
 * body estruturado são lidos do erro embrulhado via `unwrapApiError`.
 */

import { getApiErrorMessage, hasApiErrorCode, unwrapApiError } from '@/utils/apiError'

const ACTIVE_INVITE_MESSAGE =
  'Já existe um convite ativo para este médico nesta empresa. ' +
  'Se o convite foi recusado e o problema persistir, entre em contato com o suporte.'

/**
 * Código estruturado do caso "convite já ativo" (vínculo médico-empresa ainda
 * vigente: convite pendente/aceito ou dado legado). Publicado pelo backend
 * (`CompaniesError.DOCTOR_INVITE_ALREADY_ACTIVE`). Pós-gate G1 a detecção é SÓ
 * por código — não reintroduzir match por texto da mensagem.
 */
export const ACTIVE_INVITE_ERROR_CODE = 'COMPANY_DOCTOR_INVITE_ALREADY_ACTIVE'

/**
 * Mensagem exibível para uma falha de convite. O caso de convite já ativo é
 * reconhecido pelo código e exibe a mensagem curada deste módulo; o resto
 * segue o funil canônico (`ServiceError` → sua message; rede/timeout/5xx →
 * genéricos do catálogo; 4xx → message do backend; senão → `fallback`), com as
 * quebras de linha do texto do backend colapsadas.
 *
 * Como é um guard de domínio, o call site exibe o retorno direto
 * (`toast.error(getInviteErrorMessage(e, ...))`) — passá-lo como fallback de
 * `notifyError` esconderia a mensagem curada.
 */
export function getInviteErrorMessage(error: unknown, fallback: string): string {
  if (hasApiErrorCode(error, ACTIVE_INVITE_ERROR_CODE)) {
    return ACTIVE_INVITE_MESSAGE
  }

  return getApiErrorMessage(error, fallback).replace(/\n\s*/g, ' ')
}

/**
 * 409 do guardrail de identidade do convite: o CRM digitado pertence a uma
 * pessoa que já possui cadastro por OUTRO CRM (mesmo nome canônico do CFM).
 * O body estruturado alimenta o CTA "Transferir CRM para este cadastro".
 *
 * Contrato vivo no backend: `companies.service.ts#resolveInviteDoctorByCrm`
 * lança `ConflictException({ message, code: 'CRM_BELONGS_TO_EXISTING_DOCTOR',
 * cfmName, existingDoctorId, existingCrm, requestedCrm })` (minhaescala-backend
 * #2846); o `AllExceptionsFilter` repassa os campos extras do payload. O
 * bloqueio pode ser desligado no backend por `INVITE_IDENTITY_GUARDRAIL=off`
 * — aí o 409 simplesmente não chega e o convite segue o fluxo normal.
 */
export type InviteCrmConflict = {
  code: 'CRM_BELONGS_TO_EXISTING_DOCTOR'
  cfmName: string
  existingDoctorId: number
  existingCrm: { number: string; uf: string }
  requestedCrm: { number: string; uf: string }
}

export const CRM_BELONGS_TO_EXISTING_DOCTOR_CODE = 'CRM_BELONGS_TO_EXISTING_DOCTOR'

type InviteConflictErrorLike = {
  response?: { data?: Partial<InviteCrmConflict> }
}

export function getInviteCrmConflict(error: unknown): InviteCrmConflict | null {
  if (!hasApiErrorCode(error, CRM_BELONGS_TO_EXISTING_DOCTOR_CODE)) return null
  const data = (unwrapApiError(error) as InviteConflictErrorLike | null | undefined)?.response?.data
  if (data?.existingDoctorId) {
    return data as InviteCrmConflict
  }
  return null
}
